import { SetOptional } from 'type-fest';

import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useFormContext } from '@/components/react-hook-form';
import { useForm, useSubmitSuccessHandler } from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';

import { useCreateRoleMutation } from '../graphql/CreateRole.generated';
import { Operations } from '../graphql/GetRoles.generated';
import { RoleForm } from '../RoleViewer/RoleForm';
import { RoleFormData } from '../RoleViewer/RoleViewer.types';
import {
  getCreateRoleInput,
  getRoleFormValues,
} from '../RoleViewer/RoleViewer.utils';

type FormModalOptionalProps = SetOptional<FormModalProps, 'heading'>;
export type CreateRoleModalProps = FormModalOptionalProps & {
  allCapabilities: Array<{ id: string; authzedRelationName: string }>;
};

function CreateRole({
  onClose,
  allCapabilities,
  heading = 'Add new role',
  ...formModalProps
}: CreateRoleModalProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const trackUserEvent = useTrackUserEvent();
  const { formRef, handleSave } = useFormSaveHandler();
  const { handleSubmit, reset, shouldBlockNavigation, formState } =
    useFormContext<RoleFormData>();

  const [createMutation] = useCreateRoleMutation({
    refetchQueries: [Operations.Query.GetRoles],
    awaitRefetchQueries: true,
    onError: (error) => {
      showFeedback(FeedbackMessages.formSaveError);
      reportError('Could not create role', error);
    },
  });

  const onSubmit = handleSubmit(async (formData) => {
    const input = getCreateRoleInput(formData, allCapabilities);
    const result = await createMutation({
      variables: { input },
    });
    // onError handles failures, so a missing role means the save failed.
    const createdRole = result.data?.createRole;
    if (createdRole) {
      trackUserEvent('role created', {
        roleId: createdRole.id,
        canApproveClientPresentations:
          formData.generalProperties.canApproveClientPresentations,
      });
    }
    return result;
  });

  const closeModal = () => {
    reset();
    onClose();
  };

  useSubmitSuccessHandler(() => {
    closeModal();
  });

  return (
    <FormModal
      {...formModalProps}
      onClose={onClose}
      heading={heading}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton onClick={handleSave}>
            Add role
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <RoleForm variant="create" />
      </form>
    </FormModal>
  );
}

export const CreateRoleModal = ({
  allCapabilities,
  ...props
}: CreateRoleModalProps) => {
  const formMethods = useForm<RoleFormData>({
    defaultValues: getRoleFormValues(undefined), // Use empty defaults for create
  });

  return (
    <FormProvider {...formMethods}>
      <CreateRole allCapabilities={allCapabilities} {...props} />
    </FormProvider>
  );
};
